/**
 * Keeps v9-only types and declaration merging isolated while Memex can run
 * TanStack Table v8 or v9 during the feature-flagged rollout.
 */
import type {
  ColumnDef,
  ReactTable,
  Row,
  RowData,
  RowModel,
  StockFeatures,
  Table,
  TableFeature,
  TableFeatures,
  TableState,
} from '@tanstack/react-table-v9'

import type {MissingSubIssue} from '../../../api/common-contracts'
import type {AgentTask} from '../../../api/memex-items/contracts'
import type {Group} from '../../../api/memex-items/paginated-views'
import type {CustomColumnMeta} from '../../../components/table/state-providers/table-columns/types'
import type {FieldGrouping} from '../../../features/grouping/types'
import type {usePaginatedMemexItemsQueryReturnType} from '../../../state-providers/memex-items/queries/use-paginated-memex-items-query'

type QueryDataTableState = Pick<
  usePaginatedMemexItemsQueryReturnType,
  'groupedItemQueries' | 'queriesForGroups' | 'groupsById'
> & {
  subIssuesQueryData: Pick<usePaginatedMemexItemsQueryReturnType, 'subIssuesByParentId'> & {
    copilotTasksEnabled: boolean
  }
}

type MemexTableState = {
  groupQueryData: Pick<QueryDataTableState, 'groupedItemQueries' | 'queriesForGroups' | 'groupsById'>
  subIssuesQueryData: QueryDataTableState['subIssuesQueryData']
}

export type CustomGroupingRow = {
  groupQueryData: Omit<Group, 'fieldMetrics'>
  /** @deprecated Use groupingValue instead. */
  groupedValue: string
  /** @deprecated Use groupQueryData.groupMetadata instead. */
  groupedSourceObject: FieldGrouping
  /** @deprecated Use groupQueryData.totalCount instead. */
  totalCount: number
  subIssueData?: {
    missingSubIssues?: Array<MissingSubIssue>
    subIssueDepth?: number
  }
  /** When set, this row represents a virtual agent assignment task rather than a MemexProjectItem. */
  agentAssignmentTaskData?: {
    task: AgentTask
  }
}

/**
 * @link [Declaration merging](https://tanstack.com/table/latest/docs/guide/custom-features#step-2-use-declaration-merging-to-add-new-types-to-tanstack-table)
 */
declare module '@tanstack/react-table-v9' {
  interface Plugins {
    memexTableFeature: TableFeature
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface TableState_FeatureMap {
    memexTableFeature: MemexTableState
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention, unused-imports/no-unused-vars -- Must match TanStack's declaration.
  interface Row_FeatureMap<TFeatures extends TableFeatures, TData extends RowData> {
    memexTableFeature: CustomGroupingRow
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, unused-imports/no-unused-vars -- Must match TanStack's declaration.
  interface ColumnMeta<TFeatures extends TableFeatures, TData extends RowData, TValue> extends CustomColumnMeta {}
}

export const memexTableFeature: TableFeature = {}

export type MemexTableFeatures = StockFeatures & {memexTableFeature: TableFeature}
export type ColumnDefV9<TData extends object> = ColumnDef<MemexTableFeatures, TData, unknown> & {id: string}
export type RowV9<TData extends object> = Row<MemexTableFeatures, TData>
export type RowModelV9<TData extends object> = RowModel<MemexTableFeatures, TData>
export type CoreTableV9<TData extends object> = Table<MemexTableFeatures, TData>
export type TableStateV9 = TableState<MemexTableFeatures>
export type ReactTableV9<TData extends object> = ReactTable<MemexTableFeatures, TData, TableStateV9> & {
  getState: () => TableStateV9
}
